import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Coins,
  Users,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  Clock,
  Shuffle,
  Layers,
  Plus,
  MapPin,
  Shield,
  ThumbsDown,
} from "lucide-react";
import { toast } from "sonner";

function pctChange(current: number, prev: number): number | null {
  if (prev === 0) return null;
  return Math.round(((current - prev) / prev) * 100);
}

function TrendBadge({ current, prev }: { current: number; prev: number }) {
  const pct = pctChange(current, prev);
  if (pct === null) return null;
  if (pct > 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-success font-medium">
        <ArrowUpRight className="h-3 w-3" />
        +{pct}%
      </span>
    );
  if (pct < 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-destructive font-medium">
        <ArrowDownRight className="h-3 w-3" />
        {pct}%
      </span>
    );
  return <span className="text-xs text-muted-foreground">=</span>;
}

// Simple inline bar chart (no recharts dependency)
function CreditBarChart({ data }: { data: Array<{ month: string; total: number }> }) {
  const max = Math.max(...data.map((d) => d.total), 1);
  return (
    <div className="flex items-end gap-1 h-24 mt-2">
      {data.map((d) => {
        const pct = (d.total / max) * 100;
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1 group">
            <div
              className="w-full bg-primary rounded-t-sm transition-all group-hover:bg-primary/80 relative"
              style={{ height: `${Math.max(pct, 4)}%` }}
              title={`${d.total} crédits`}
            />
            <span className="text-[10px] text-muted-foreground truncate w-full text-center">
              {d.month.split("-")[1]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Horizontal bar chart for lead distribution per pro
function LeadDistributionChart({
  artisans,
}: {
  artisans: Array<{ companyName: string; unlocks: number }>;
}) {
  if (artisans.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        Aucune donnée
      </p>
    );
  }
  const max = Math.max(...artisans.map((a) => a.unlocks), 1);
  const avg =
    artisans.reduce((s, a) => s + a.unlocks, 0) / artisans.length;
  const threshold = avg * 2;

  return (
    <div className="space-y-2">
      {artisans.map((a) => {
        const pct = (a.unlocks / max) * 100;
        const isHot = a.unlocks >= threshold;
        return (
          <div key={a.companyName} className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-32 truncate text-right shrink-0">
              {a.companyName}
            </span>
            <div className="flex-1 h-5 bg-muted rounded-sm overflow-hidden relative">
              <div
                className={`h-full rounded-sm transition-all ${
                  isHot
                    ? "bg-destructive/80"
                    : "bg-primary/70"
                }`}
                style={{ width: `${Math.max(pct, 3)}%` }}
              />
            </div>
            <span
              className={`text-xs font-medium w-8 text-right ${
                isHot ? "text-destructive" : ""
              }`}
            >
              {a.unlocks}
            </span>
            {isHot && (
              <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
            )}
          </div>
        );
      })}
      {threshold > 0 && (
        <p className="text-[11px] text-muted-foreground mt-1">
          Seuil d'alerte : ≥ {Math.ceil(threshold)} débloquages (2× la
          moyenne de {Math.round(avg)})
        </p>
      )}
    </div>
  );
}

interface QualityScoreRow {
  category: string;
  city: string;
  bronze: number;
  silver: number;
  gold: number;
  total: number;
}

const TIER_CONFIG = {
  GOLD: { label: "Gold", color: "bg-yellow-400/20 text-yellow-700 border-yellow-400/30" },
  SILVER: { label: "Silver", color: "bg-slate-300/20 text-slate-700 border-slate-300/30" },
  BRONZE: { label: "Bronze", color: "bg-amber-600/20 text-amber-800 border-amber-600/30" },
} as const;

function QualityScoreDistribution({ data }: { data: QualityScoreRow[] }) {
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8">
        <Shield className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">
          Aucune donnée de scoring qualité disponible
        </p>
      </div>
    );
  }

  const alertRows = data.filter(
    (r) => r.total > 0 && (r.bronze / r.total) * 100 > 60
  );

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Catégorie</TableHead>
            <TableHead>Ville</TableHead>
            <TableHead className="text-center">
              <Badge variant="outline" className={TIER_CONFIG.GOLD.color}>Gold</Badge>
            </TableHead>
            <TableHead className="text-center">
              <Badge variant="outline" className={TIER_CONFIG.SILVER.color}>Silver</Badge>
            </TableHead>
            <TableHead className="text-center">
              <Badge variant="outline" className={TIER_CONFIG.BRONZE.color}>Bronze</Badge>
            </TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">% Bronze</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => {
            const bronzePct = row.total > 0 ? Math.round((row.bronze / row.total) * 100) : 0;
            const isAlert = bronzePct > 60;
            return (
              <TableRow
                key={`${row.category}-${row.city}`}
                className={isAlert ? "bg-destructive/5" : ""}
              >
                <TableCell className="font-medium text-sm">{row.category}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {row.city}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="secondary">{row.gold}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="secondary">{row.silver}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="secondary">{row.bronze}</Badge>
                </TableCell>
                <TableCell className="text-right text-sm font-medium">{row.total}</TableCell>
                <TableCell className="text-right">
                  <span className={`text-sm font-medium ${isAlert ? "text-destructive" : ""}`}>
                    {bronzePct}%
                    {isAlert && <AlertTriangle className="inline h-3.5 w-3.5 ml-1" />}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {alertRows.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-destructive flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            {alertRows.length} catégorie{alertRows.length > 1 ? "s" : ""} avec &gt; 60% de leads Bronze — investigation qualité recommandée
          </p>
          {alertRows.map((r) => (
            <div
              key={`alert-${r.category}-${r.city}`}
              className="flex items-center gap-3 p-2 rounded-md bg-destructive/5 border border-destructive/20"
            >
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{r.category}</p>
                <p className="text-xs text-muted-foreground">
                  {r.city} — {Math.round((r.bronze / r.total) * 100)}% Bronze ({r.bronze}/{r.total})
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface LostReasonRow {
  reason: string;
  count: number;
}

function LostReasonsWidget({ data }: { data: LostReasonRow[] }) {
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8">
        <ThumbsDown className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">
          Aucune donnée de perte disponible
        </p>
      </div>
    );
  }

  const total = data.reduce((s, r) => s + r.count, 0);
  const top3 = data.slice(0, 3);

  return (
    <div className="space-y-3">
      {top3.map((row, idx) => {
        const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
        return (
          <div key={row.reason} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium flex items-center gap-2">
                <Badge variant="outline" className="text-xs w-5 h-5 flex items-center justify-center p-0">
                  {idx + 1}
                </Badge>
                {row.reason}
              </span>
              <span className="text-sm text-muted-foreground">
                {row.count} ({pct}%)
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-destructive/60 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
      {data.length > 3 && (
        <p className="text-xs text-muted-foreground">
          + {data.length - 3} autre{data.length - 3 > 1 ? "s" : ""} raison{data.length - 3 > 1 ? "s" : ""}
        </p>
      )}
      <p className="text-xs text-muted-foreground border-t pt-2">
        Total : {total} lead{total > 1 ? "s" : ""} perdu{total > 1 ? "s" : ""} cette semaine
      </p>
    </div>
  );
}

export default function TravauxBTPDashboard() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const { data, isLoading } = trpc.admin.travauxbtp.getDashboard.useQuery();
  const { data: rotationStats } = trpc.ops.getRotationStats.useQuery();
  const { data: leadDist } = trpc.ops.getLeadDistribution.useQuery();
  const { data: understaffed, refetch: refetchUnderstaffed } =
    trpc.ops.getUnderstaffedProjects.useQuery();
  const { data: lostReasons } = trpc.admin.travauxbtp.getLostReasonBreakdown.useQuery();

  const overrideMutation = trpc.ops.overrideMaxUnlocks.useMutation({
    onSuccess: (result) => {
      toast.success(
        `Slots mis à jour : ${result.previousMaxUnlocks} → ${result.maxUnlocks} pour "${result.title}"`
      );
      refetchUnderstaffed();
    },
    onError: (err) => toast.error(err.message),
  });

  const [overrideProjectId, setOverrideProjectId] = useState<string | null>(null);

  if (isLoading || !data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Chargement...
        </div>
      </AdminLayout>
    );
  }

  const monthPct = pctChange(data.creditsSoldMonth, data.creditsSoldPrevMonth);
  const quarterPct = pctChange(data.creditsSoldQuarter, data.creditsSoldPrevQuarter);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard TravauxBTP</h1>
          <p className="text-muted-foreground text-sm mt-1">
            KPIs marketplace artisans — crédits, projets, artisans actifs
          </p>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Crédits ce mois</CardTitle>
              <Coins className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.creditsSoldMonth.toLocaleString("fr-FR")}</div>
              <TrendBadge current={data.creditsSoldMonth} prev={data.creditsSoldPrevMonth} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Crédits ce trimestre</CardTitle>
              <Coins className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.creditsSoldQuarter.toLocaleString("fr-FR")}</div>
              <TrendBadge current={data.creditsSoldQuarter} prev={data.creditsSoldPrevQuarter} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Artisans actifs</CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{data.activeArtisans}</div>
              <span className="text-xs text-muted-foreground">≥ 1 achat / 30j</span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Taux acceptation devis</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">{data.acceptanceRate}%</div>
              <span className="text-xs text-muted-foreground">{data.acceptedQuotes}/{data.totalQuotes} devis</span>
            </CardContent>
          </Card>
        </div>

        {/* Second row KPIs */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Funnel projets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 flex-wrap">
                {[
                  { label: "Publiés", count: data.projectFunnel.published, color: "bg-warning/10 text-warning border-warning/20" },
                  { label: "Matchés", count: data.projectFunnel.matched, color: "bg-primary/10 text-primary border-primary/20" },
                  { label: "Terminés", count: data.projectFunnel.completed, color: "bg-success/10 text-success border-success/20" },
                ].map((step, idx, arr) => (
                  <div key={step.label} className="flex items-center gap-2">
                    <div className={`flex flex-col items-center px-3 py-1.5 rounded-lg border text-center ${step.color}`}>
                      <span className="text-lg font-bold">{step.count}</span>
                      <span className="text-xs">{step.label}</span>
                    </div>
                    {idx < arr.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Coins className="h-4 w-4 text-warning" />
                Revenu moyen / artisan / mois
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {data.avgRevenuePerArtisan > 0
                  ? `${data.avgRevenuePerArtisan.toLocaleString("fr-FR")} crédits`
                  : "—"}
              </div>
              <span className="text-xs text-muted-foreground">Basé sur les 30 derniers jours</span>
            </CardContent>
          </Card>
        </div>

        {/* Top artisans + Credit evolution */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Top 10 artisans actifs (30 derniers jours)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Artisan</TableHead>
                    <TableHead className="text-right">Débloquages</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.topArtisans.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                        Aucune activité
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.topArtisans.map((a, i) => (
                      <TableRow key={a.companyId}>
                        <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                        <TableCell className="font-medium">{a.companyName}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{a.unlocks}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                Évolution crédits vendus (6 mois)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.creditEvolution.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Aucune donnée</p>
              ) : (
                <CreditBarChart data={data.creditEvolution} />
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Rotation & Slots Monitoring ── */}
        <div>
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2 mb-4">
            <Shuffle className="h-5 w-5 text-primary" />
            Monitoring rotation pros & slots
          </h2>
        </div>

        {/* Rotation KPI cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Projets complets
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                {rotationStats?.projectsComplete ?? data.projectFunnel.completed}
              </div>
              <span className="text-xs text-muted-foreground">
                Slots remplis
              </span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                En attente de pros
              </CardTitle>
              <Clock className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-warning">
                {rotationStats?.projectsWaiting ??
                  data.projectFunnel.published + data.projectFunnel.matched}
              </div>
              <span className="text-xs text-muted-foreground">
                &lt; max slots remplis
              </span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Taux de remplissage
              </CardTitle>
              <Layers className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {rotationStats
                  ? `${Math.round(rotationStats.avgFillRate * 100)}%`
                  : "—"}
              </div>
              <span className="text-xs text-muted-foreground">
                {rotationStats
                  ? `${rotationStats.totalActive} projets actifs`
                  : "Chargement..."}
              </span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Temps moyen remplissage
              </CardTitle>
              <Clock className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {rotationStats?.avgTimeToFillHours != null
                  ? rotationStats.avgTimeToFillHours < 24
                    ? `${rotationStats.avgTimeToFillHours}h`
                    : `${Math.round(rotationStats.avgTimeToFillHours / 24)}j`
                  : "—"}
              </div>
              <span className="text-xs text-muted-foreground">
                {rotationStats?.avgTimeToFillHours != null
                  ? "Pour remplir tous les slots"
                  : "Pas assez de données"}
              </span>
            </CardContent>
          </Card>
        </div>

        {/* Lead distribution + Alerts */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Lead distribution histogram — weekly */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                Distribution leads par pro (cette semaine)
                {leadDist && (
                  <Badge variant="secondary" className="ml-auto text-xs font-normal">
                    {leadDist.totalLeadsThisWeek} leads · {leadDist.prosWithLeads} pros · moy. {leadDist.avgLeadsPerPro}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leadDist ? (
                <LeadDistributionChart
                  artisans={leadDist.distribution.map((d) => ({
                    companyName: d.companyName,
                    unlocks: d.leadsThisWeek,
                  }))}
                />
              ) : (
                <LeadDistributionChart artisans={data.topArtisans} />
              )}
            </CardContent>
          </Card>

          {/* Rotation alerts — weekly */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning" />
                Alertes rotation (cette semaine)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const source = leadDist
                  ? leadDist.distribution.map((d) => ({
                      companyId: d.companyId,
                      companyName: d.companyName,
                      count: d.leadsThisWeek,
                    }))
                  : data.topArtisans.map((a) => ({
                      companyId: a.companyId,
                      companyName: a.companyName,
                      count: a.unlocks,
                    }));

                if (source.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Aucune alerte
                    </p>
                  );
                }
                const avg =
                  source.reduce((s, a) => s + a.count, 0) / source.length;
                const threshold = avg * 2;
                const hotPros = source.filter((a) => a.count >= threshold);
                if (hotPros.length === 0) {
                  return (
                    <div className="flex flex-col items-center gap-2 py-6">
                      <CheckCircle2 className="h-8 w-8 text-success" />
                      <p className="text-sm text-muted-foreground">
                        Rotation équilibrée — aucun pro ne dépasse 2× la
                        moyenne
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-2">
                    {hotPros.map((a) => (
                      <div
                        key={a.companyId}
                        className="flex items-center gap-3 p-2 rounded-md bg-destructive/5 border border-destructive/20"
                      >
                        <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {a.companyName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {a.count} leads — {Math.round(
                              (a.count / avg) * 100
                            ) / 100}× la moyenne
                          </p>
                        </div>
                        <Badge variant="destructive" className="shrink-0">
                          {a.count}
                        </Badge>
                      </div>
                    ))}
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Seuil : {Math.ceil(threshold)} leads (moy.{" "}
                      {Math.round(avg)})
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Quality Score Distribution + Lost Reasons */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                Distribution scores qualité leads par catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <QualityScoreDistribution data={data.qualityScoreDistribution ?? []} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ThumbsDown className="h-4 w-4 text-destructive" />
                Top raisons de perte (cette semaine)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LostReasonsWidget data={lostReasons ?? []} />
            </CardContent>
          </Card>
        </div>

        {/* Stale projects — understaffed > 48h */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-warning" />
              Projets en attente &gt; 48h (&lt; 3 pros)
              {understaffed && understaffed.length > 0 && (
                <Badge variant="destructive" className="ml-auto">
                  {understaffed.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {!understaffed ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                Chargement...
              </p>
            ) : understaffed.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6">
                <CheckCircle2 className="h-8 w-8 text-success" />
                <p className="text-sm text-muted-foreground">
                  Tous les projets &gt; 48h ont au moins 3 pros
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Projet</TableHead>
                    <TableHead>Localisation</TableHead>
                    <TableHead className="text-center">Slots</TableHead>
                    <TableHead className="text-center">Ancienneté</TableHead>
                    <TableHead className="text-center">Contact OPS</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {understaffed.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm truncate max-w-[200px]">
                            {p.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {p.category}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {p.city}, {p.department}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            p.unlocksCount === 0 ? "destructive" : "secondary"
                          }
                        >
                          {p.unlocksCount}/{p.maxUnlocks}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">
                          {p.hoursStale < 24
                            ? `${p.hoursStale}h`
                            : `${Math.round(p.hoursStale / 24)}j`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            p.opsContactStatus === "CONTACTED"
                              ? "default"
                              : p.opsContactStatus === "NOT_NEEDED"
                                ? "secondary"
                                : "outline"
                          }
                        >
                          {p.opsContactStatus ?? "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canAct ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={
                              overrideMutation.isPending &&
                              overrideProjectId === p.id
                            }
                            onClick={() => {
                              setOverrideProjectId(p.id);
                              overrideMutation.mutate({
                                projectId: p.id,
                                newMaxUnlocks: p.maxUnlocks + 1,
                              });
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Slot
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
